import { useMemo } from 'react'
import type { TextUIPart, UIMessage } from 'ai'
import { isToolUIPart } from 'ai'

export type LlmChatAssistantStreamOptions = {
  isActiveAssistantTurn: boolean
  isAwaitingAssistantFirstChunk: boolean
  isAssistantResponseStreaming: boolean
}

export function useLlmChatAssistantStreamState(
  message: UIMessage,
  options: LlmChatAssistantStreamOptions
) {
  const {
    isActiveAssistantTurn,
    isAwaitingAssistantFirstChunk,
    isAssistantResponseStreaming,
  } = options

  const lastTextPartIndex = useMemo(() => {
    for (let idx = message.parts.length - 1; idx >= 0; idx -= 1) {
      if (message.parts[idx].type === 'text') {
        return idx
      }
    }
    return -1
  }, [message.parts])

  const textParts = useMemo(
    () =>
      message.parts.filter((part): part is TextUIPart => part.type === 'text'),
    [message.parts]
  )

  const lastTextPart = textParts[textParts.length - 1]
  const hasAssistantTextParts = textParts.length > 0
  const lastTextPartIsStreaming = lastTextPart?.state === 'streaming'

  const hasAssistantToolWorkInProgress = useMemo(
    () =>
      message.parts.some(
        (part) =>
          isToolUIPart(part) &&
          part.state !== 'output-available' &&
          part.state !== 'output-error'
      ),
    [message.parts]
  )

  const shouldPulseWhileAwaitingFirstChunk =
    isActiveAssistantTurn && isAwaitingAssistantFirstChunk
  const shouldPulseWhileTextStreaming =
    isActiveAssistantTurn && lastTextPartIsStreaming
  const shouldPulseWhileStreamingBeforeAssistantText =
    isActiveAssistantTurn &&
    isAssistantResponseStreaming &&
    !hasAssistantTextParts &&
    !hasAssistantToolWorkInProgress

  const showTextCursor =
    (shouldPulseWhileAwaitingFirstChunk ||
      shouldPulseWhileTextStreaming ||
      shouldPulseWhileStreamingBeforeAssistantText) &&
    (!hasAssistantTextParts || lastTextPartIsStreaming)

  return {
    lastTextPartIndex,
    showTextCursor,
    hasAssistantTextParts,
  }
}
